import { sync } from '@genesislcap/foundation-utils';
import { html, repeat, when, ref } from '@microsoft/fast-element';
import { deleteIcon } from '../../../styles';
import { IArteriaNegotiation, INegotiation } from '../../../types';
import type { ExecutionCard } from './execution-card';
import { expandPlusIcon, collapsePlusIcon } from '../../../styles';
import { eventDetail, formatNumberToDate } from '../../../utils/formatting';

export enum EXECUTION_TABS {
  RFQS_TAB = 'RFQs',
  RFQS_TAB_PANEL = 'RFQsPanel',
  AUTO_EXECUTE_TAB = 'autoExecuteContracts',
  AUTO_EXECUTE_TAB_PANEL = 'autoExecuteContractsPanel',
  AWAITING_KEY_METRICS = 'Awaiting Confirmation',
  EXPIRED = 'Expired',
}

export const ExecutionCardTemplate = html<ExecutionCard>`
  <zero-card class="execution-card">
    <zero-tabs ${ref('executionPageTabs')} activeId=${EXECUTION_TABS.RFQS_TAB} class="parent-tab">
      <zero-tab slot='tab' id=${EXECUTION_TABS.RFQS_TAB} @click=${(x) => x.rfqsTab()}>RFQ Method
        ${when(
          (x, c) => x.negotiationCounter + x.expiredNegotiationCounter > 0,
          html`
            <span class="rowCount">
              ${(x) => x.negotiationCounter + x.expiredNegotiationCounter}
            </span>
          `
        )}
      </zero-tab>

      <zero-tab slot='tab' ${ref('autoExecuteTabElement')} id=${
  EXECUTION_TABS.AUTO_EXECUTE_TAB
} @click=${(x) => x.autoExecuteTab()}>Auto-Execute Contracts 
                ${when(
                  (x, c) => x.autoExecutedNegotiationsCounter > 0,
                  html`
                    <span class="rowCount">${(x) => x.autoExecutedNegotiationsCounter}</span>
                  `
                )}
      </zero-tab>
      <zero-tab-panel slot='tabpanel' id=${EXECUTION_TABS.RFQS_TAB_PANEL}>
        <zero-tabs class="rfq-tabs"> 
          <zero-tab @click=${(x) => x.rfqTabSelected(EXECUTION_TABS.AWAITING_KEY_METRICS)}>
            ${EXECUTION_TABS.AWAITING_KEY_METRICS}
            ${when(
              (x, c) => x.negotiationCounter > 0,
              html`
                <span class="rowCount">${(x) => x.negotiationCounter}</span>
              `
            )}
          </zero-tab>
          <zero-tab @click=${(x) => x.rfqTabSelected(EXECUTION_TABS.EXPIRED)}>
            ${EXECUTION_TABS.EXPIRED}
            ${when(
              (x, c) => x.expiredNegotiationCounter > 0,
              html`
                <span class="rowCount">${(x) => x.expiredNegotiationCounter}</span>
              `
            )}
          </zero-tab>
          <zero-tab-panel>
            ${repeat(
              (x) => x.negotiations,
              html<IArteriaNegotiation>`
                ${when(
                  (x) =>
                    ![
                      'EXPIRED',
                      'REJECTED',
                      'FULL_DELETED',
                      'BUYER_DELETED',
                      'SELLER_DELETED',
                      'IN_PROGRESS_ARTERIA',
                      'READY_FOR_TRADE_EXECUTION',
                      'REVIEW_EXECUTION_CHECKLIST',
                    ].includes(x.NEGOTIATION_STATUS),
                  html`
                    <div
                      class="negotiation ${sync((x, c) =>
                        c.parent.selectedNegotiation?.NEGOTIATION_ID == x.NEGOTIATION_ID
                          ? 'selected'
                          : ''
                      )}"
                      @click="${(x, c) => c.parent.setSelectedNegotiation(x)}"
                    >
                      ${when(
                        (x, c) =>
                          c.parent.executionNotifications?.find(
                            (e) => e.NEGOTIATION_ID === x.NEGOTIATION_ID
                          ),
                        html`
                          <span class="not-visualized"></span>
                        `
                      )}
                      <span class="ticker">${(x) => x.TICKER}</span>
                      <span class="details">
                        ${(x, c) => c.parent.getCompany(x)} | ${(x) => x.ISO} | ${(x) => x.NAME} |
                        ${(x) => x.MEGAWATT_AC}MW
                      </span>
                      <span class="status ${(x, c) => c.parent.statusClass(x.NEGOTIATION_STATUS)}">
                        ${(x, c) => c.parent.readableStatus(x.NEGOTIATION_STATUS)}
                      </span>
                    </div>
                  `
                )}
              `
            )}
          </zero-tab-panel>
          <zero-tab-panel>
            ${repeat(
              (x) => x.negotiations,
              html<IArteriaNegotiation>`
                ${when(
                  (x, c) =>
                    x.NEGOTIATION_STATUS === 'EXPIRED' ||
                    x.NEGOTIATION_STATUS ===
                      (c.parent.isSellerCompany(x) ? 'BUYER_DELETED' : 'SELLER_DELETED'),
                  html`
                    <div
                      class="negotiation expired ${sync((x, c) =>
                        c.parent.selectedNegotiation?.NEGOTIATION_ID == x.NEGOTIATION_ID
                          ? 'selected'
                          : ''
                      )}"
                      @click="${(x, c) => c.parent.setSelectedNegotiation(x)}"
                    >
                      ${when(
                        (x, c) =>
                          c.parent.executionNotifications?.find(
                            (e) => e.NEGOTIATION_ID === x.NEGOTIATION_ID
                          ),
                        html`
                          <span class="not-visualized"></span>
                        `
                      )}
                      <span class="ticker">${(x) => x.TICKER}</span>
                      <span class="details">
                        ${(x, c) => c.parent.getCompany(x)} | ${(x) => x.ISO} | ${(x) => x.NAME} |
                        ${(x) => x.MEGAWATT_AC}MW
                        <span
                          class="bin"
                          @click="${(x, c) => c.parent.deleteNegotiation(x.NEGOTIATION_ID)}"
                        >
                          ${deleteIcon('', '10px', '13px')}
                        </span>
                      </span>
                    </div>
                  `
                )}
              `
            )}
          </zero-tab-panel>
      </zero-tab-panel>
      <zero-tab-panel slot='tabpanel' id=${EXECUTION_TABS.AUTO_EXECUTE_TAB_PANEL}>
        <fast-accordion>
          ${repeat(
            (x) => x.uniqueDates,
            html`
              <fast-accordion-item expanded=${(x, c) => c.parent.uniqueDates[0] === x}>
                <span slot="heading" class="heading">${(x, c) => c.parent.getHeader(x)}</span>
                ${expandPlusIcon('expanded-icon')} ${collapsePlusIcon('collapsed-icon')}
                ${repeat(
                  (x, c) => c.parent.autoExecutedNegotiations,
                  html<INegotiation>`
                    ${when(
                      (x, c) => formatNumberToDate(x.CREATED_AT) === c.parent,
                      html<INegotiation>`
                        <div
                          class="negotiation ${sync((x, c) =>
                            c.parentContext.parent.selectedNegotiation &&
                            c.parentContext.parent.selectedNegotiation.NEGOTIATION_ID ==
                              x.NEGOTIATION_ID
                              ? 'selected'
                              : ''
                          )}"
                          @click="${(x, c) => c.parentContext.parent.setSelectedNegotiation(x)}"
                        >
                          ${when(
                            (x, c) =>
                              c.parentContext?.parent?.executionNotifications?.find(
                                (e) => e.NEGOTIATION_ID === x.NEGOTIATION_ID
                              ),
                            html`
                              <span class="not-visualized"></span>
                            `
                          )}

                          <span class="ticker">${(x) => x.VPPA_TICKER}</span>
                          <span class="details">
                            ${(x, c) => c.parentContext.parent.getCompany(x)} | ${(x) => x.ISO} |
                            ${(x) => x.PROJECT_NAME} | ${(x) => x.OFFERED_CAPACITY}MW
                          </span>
                          <span
                            class="status ${(x, c) =>
                              c.parentContext.parent.statusClass(x.NEGOTIATION_STATUS)}"
                          >
                            ${(x, c) => c.parentContext.parent.readableStatus(x.NEGOTIATION_STATUS)}
                          </span>
                        </div>
                      `
                    )}
                  `
                )}
              </fast-accordion-item>
            `
          )}
        </fast-accordion>
      </zero-tab-panel>
    </zero-tabs>
    <execution-drilldown :negotiation=${(x) => x.selectedNegotiation} @reopenNegotiationConfirm="${(
  x
) => x.openBackToNegotiationConfirm()}"></execution-drilldown>
    ${when(
      (x) => x.showBackToNegotiatonConfirm,
      html`
        <close-popup
          @closeModal="${(x, c) => x.closeBackToNegotiationConfirm(eventDetail(c))}"
          class="close-popup"
          header=${(x) => x.backToNegotiationHeader}
          description=${(x) => x.backToNegotiationDescription}
        ></close-popup>
      `
    )}
  </zero-card>
`;
